"use client";

import { Package, Hammer, Briefcase, Layers } from "@semo/icons";
import { CATEGORY_TYPE_OPTIONS } from "@semo/api-contracts";

/**
 * Category styling configuration
 * Centralized styling rules for different category types
 */
export interface CategoryStyle {
  icon: typeof Package;
  bg: string;
  text: string;
  border: string;
  label: string;
}

/**
 * Get styling configuration for a category type
 *
 * Maps category values to visual styling (colors, icons, labels)
 * Uses OpenAPI CATEGORY_TYPE_OPTIONS as single source of truth for labels
 *
 * @param category - The category type (e.g., 'product', 'construction')
 * @returns Styling configuration with icon, colors, and label
 */
export function getCategoryStyles(category: string): CategoryStyle {
  // Use OpenAPI types for label lookup (Single Source of Truth)
  const categoryOption = CATEGORY_TYPE_OPTIONS.find(
    (opt) => opt.value === category,
  );
  const label = categoryOption?.label ?? category;

  switch (category) {
    case "product":
      return {
        icon: Package,
        bg: "bg-primary/10",
        text: "text-primary",
        border: "border-primary/20",
        label,
      };
    case "construction":
      return {
        icon: Hammer,
        bg: "bg-orange-100",
        text: "text-orange-600",
        border: "border-orange-200",
        label,
      };
    case "manufacturing":
      return {
        icon: Briefcase,
        bg: "bg-purple-100",
        text: "text-purple-600",
        border: "border-purple-200",
        label,
      };
    case "used":
      return {
        icon: Package,
        bg: "bg-green-100",
        text: "text-green-600",
        border: "border-green-200",
        label,
      };
    default:
      return {
        icon: Layers,
        bg: "bg-gray-100",
        text: "text-gray-600",
        border: "border-gray-200",
        label: category,
      };
  }
}
